import React, {FC, memo} from "react";
import {Timeline, Typography, Empty} from "antd";
import {
    SendOutlined,
    CheckCircleFilled,
    WarningOutlined,
    ClockCircleOutlined,
    QuestionCircleOutlined,
    LinkOutlined,
} from "@ant-design/icons";
import {green, orange, red, grey} from "@ant-design/colors";
import i18n from "../../../services/i18n";

const {Text} = Typography;

type MessageHistoryData = {
    Comment: string;
    EventAt: number;
    EventType: string;
    State: string;
    Useragent: string;
};

type EmailTrackingTimelineProps = {
    messageHistory?: {
        Count: number;
        Data: Array<MessageHistoryData>;
    } | null;
    messageStatus?: {
        ArrivedAt: string;
        Status: string;
        AttemptCount: number;
    } | null;
    createDate?: string | null;
};

const getEventIcon = (eventType: string): React.ReactNode => {
    switch (eventType.toLowerCase()) {
        case "sent":
            return <SendOutlined style={{color: green[5]}} />;
        case "opened":
            return <CheckCircleFilled style={{color: green[5]}} />;
        case "clicked":
            return <LinkOutlined style={{color: green[5]}} />;
        case "bounce":
        case "blocked":
        case "spam":
        case "hardbounced":
            return <WarningOutlined style={{color: red[5]}} />;
        case "softbounced":
        case "deferred":
            return <ClockCircleOutlined style={{color: orange[5]}} />;
        default:
            return <QuestionCircleOutlined style={{color: grey[5]}} />;
    }
};

const getEventLabel = (eventType: string): string => {
    switch (eventType.toLowerCase()) {
        case "sent":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.sent();
        case "opened":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.opened();
        case "clicked":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.clicked();
        case "bounce":
        case "hardbounced":
        case "softbounced":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.bounced();
        case "blocked":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.blocked();
        case "spam":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.spam();
        case "unsub":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.unsub();
        case "queued":
            return i18n.containers.notifications.EmailDetailModal.eventTypes.queued();
        default:
            return i18n.containers.notifications.EmailDetailModal.eventTypes.unknown();
    }
};

const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const EmailTrackingTimeline: FC<EmailTrackingTimelineProps> = ({
    messageHistory,
    messageStatus,
    createDate,
}) => {
    const hasHistoryData = messageHistory && messageHistory.Data && messageHistory.Data.length > 0;

    if (!hasHistoryData && !createDate) {
        return <Empty description={i18n.containers.notifications.EmailDetailModal.noTrackingData()} />;
    }

    // Ereignisse chronologisch sortieren
    const sortedEvents = hasHistoryData
        ? [...messageHistory.Data].sort((a, b) => a.EventAt - b.EventAt)
        : [];

    // Das lokale createDate-"Gesendet" nur zeigen, wenn die Mailjet-Historie noch KEIN
    // eigenes "sent"-Event liefert — sonst erschiene "Gesendet" doppelt (einmal aus der
    // lokal protokollierten Sendezeit, einmal aus dem Mailjet-Event). Ohne Mailjet-Historie
    // bleibt das createDate-Item als Fallback erhalten.
    const historyHasSentEvent = sortedEvents.some(
        (event) => event.EventType.toLowerCase() === "sent",
    );
    const showCreateDateSent = Boolean(createDate) && !historyHasSentEvent;

    return (
        <Timeline>
            {showCreateDateSent && (
                <Timeline.Item dot={<SendOutlined style={{color: green[5]}} />}>
                    <Text strong>{i18n.containers.notifications.EmailDetailModal.eventTypes.sent()}</Text>
                    <br />
                    <Text type="secondary">
                        {new Date(createDate).toLocaleString("de-DE", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </Text>
                </Timeline.Item>
            )}
            {sortedEvents.map((event, index) => (
                <Timeline.Item key={index} dot={getEventIcon(event.EventType)}>
                    <Text strong>{getEventLabel(event.EventType)}</Text>
                    <br />
                    <Text type="secondary">{formatTimestamp(event.EventAt)}</Text>
                    {event.Comment && (
                        <>
                            <br />
                            <Text type="secondary" style={{fontSize: 12}}>{event.Comment}</Text>
                        </>
                    )}
                </Timeline.Item>
            ))}
        </Timeline>
    );
};

export default memo(EmailTrackingTimeline);
